import { selectCurrentHotelId, selectCurrentHotelName } from 'accessors'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import CheckFlatIcon from 'components/ReusableComponents/Icons/CheckFlat'
import { CrossIcon } from 'components/ReusableComponents/Icons/Cross'
import InfoIcon from 'components/ReusableComponents/Icons/Info'
import PaperplaneIcon from 'components/ReusableComponents/Icons/PaperPlane'
import ScheduledIcon from 'components/ReusableComponents/Icons/Scheduled'
import FilePreview from 'components/ReusableComponents/Messaging/ConversationBubble/FilePreview'
import ImageGallery from 'components/ReusableComponents/Messaging/ConversationBubble/ImageGallery'
import Toggle from 'components/ReusableComponents/Toggle'
import ScheduleDescription from 'features/Announcements/AnnouncementConfirmation/ScheduleDescription'
import AnnouncementResults from 'features/Announcements/AnnouncementResults'
import { customCampaignStatusTypes as statusTypes } from 'features/CustomCampaigns/helpers'
import usePlausible from 'hooks/usePlausible'
import moment from 'moment-timezone'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import campaignApi from 'services/api/campaign'
import config from 'services/config'

import placeholders from './constants'
import styles from './ConversationBubble.module.scss'
import Initials from './Initials'

const ConversationBubble = ({
  messageTemplate,
  deliveryRemaining,
  deliverySize,
  deliveryFinishDate,
  messageMediaUrls,
  addedBy,
  deliveryStartDate,
  addedOn,
  customDeliveryStatus,
  contentId,
  isNewMessage,
  audience,
  recurringAnnouncementId,
  onClickEdit,
  isActive,
  enableAnnouncement,
  disableAnnouncement,
  cancelAnnouncement,
  repeatKind,
  daysOfWeek
}) => {
  const timeZoneId = useSelector(s => s.hotelDetails.ianaTimeZoneId)
  const isDemoMode = useSelector(s => s.login.isDemoMode)
  const hotelId = useSelector(selectCurrentHotelId)
  const currentHotelName = useSelector(selectCurrentHotelName)

  const [focusedImage, setFocusedImage] = useState()
  const [statusOverwrite, setStatusOverwrite] = useState(null)
  const [isResultsVisible, setIsResultsVisible] = useState(false)

  const [isShowingCancelDialog, setIsShowingCancelDialog] = useState(false)

  const getImgUrl = fileName => `${config.inviteMediaBaseUrl}/${hotelId}/${fileName}`

  const { postEvent, plausibleEvents } = usePlausible()

  const hotelName = isDemoMode ? 'Directful Demo Hotel' : currentHotelName

  function handleClickShowResults() {
    postEvent(plausibleEvents.ViewAnnouncementResults, {
      contentId
    })

    setIsResultsVisible(!isResultsVisible)
  }

  function handleCancel() {
    campaignApi
      .updateCustomCampaign({
        contentId,
        customDeliveryStatus:
          customDeliveryStatus === statusTypes.Approved
            ? statusTypes.Deleted
            : statusTypes.Interrupted
      })
      .then(data => {
        setStatusOverwrite(data.customDeliveryStatus)
      })
  }

  const status = statusOverwrite || customDeliveryStatus

  const approxDeliveryEndDate = moment(deliveryStartDate).add(
    Math.floor(deliverySize / 30) * 2 + 2,
    'minutes'
  )

  const deliveryTimeDiff = moment(approxDeliveryEndDate.format('MMM DD, h:mm A')).diff(
    moment(moment().tz(timeZoneId).format('MMM DD, h:mm A')),
    'minutes'
  )

  return (
    <>
      {isShowingCancelDialog && (
        <ConfirmationDialog
          title={'Cancel Recurring Announcement'}
          onClose={() => setIsShowingCancelDialog(false)}
          onConfirm={() => {
            cancelAnnouncement()
            setIsShowingCancelDialog(false)
          }}
          positiveMessage="Cancel series"
          negativeMessage="No, go back"
          hasDangerousAction
        >
          <p>Would you like to cancel the announcement series?</p>
        </ConfirmationDialog>
      )}
      <div
        className={[
          styles.containerSecondary,
          messageMediaUrls.length > 0 ? styles.containerWithMedia : '',
          isNewMessage ? styles.newMessage : ''
        ].join(' ')}
        id={`message-${contentId}`}
      >
        {isResultsVisible && (
          <AnnouncementResults
            contentId={contentId}
            onClose={() => setIsResultsVisible(false)}
          />
        )}
        <div className={styles.initialsHolder}>
          <Initials senderName={addedBy} />
        </div>
        <div className={styles.contentHolder}>
          <div>
            {messageMediaUrls.length > 0 && (
              <div className={styles.mediaHolder}>
                {messageMediaUrls.map(m => (
                  <FilePreview
                    key={m}
                    src={getImgUrl(m)}
                    onClick={() => setFocusedImage(getImgUrl(m))}
                    showFileName
                    extension={getImgUrl(m)
                      .match(/\.([a-z]+)$/)?.[1]
                      ?.toLowerCase()}
                  />
                ))}
              </div>
            )}
            <div className={styles.starterMain}>
              <div className={styles.starterBubble}>
                <div className={styles.bubbleHeader}>
                  <div className={styles.name}>{addedBy} - </div>
                  <div className={styles.time}>
                    Added on {moment(addedOn).format('MMM DD, h:mm A')}
                  </div>
                  {recurringAnnouncementId && status === statusTypes.Approved && (
                    <div className={styles.toggleHolder}>
                      <span className={styles.toggleLabel}>Enabled</span>
                      <Toggle
                        checked={isActive}
                        handleChange={isActive ? disableAnnouncement : enableAnnouncement}
                      />
                    </div>
                  )}
                </div>

                <div className={styles.text}>
                  {messageTemplate &&
                    messageTemplate
                      .replace(placeholders.hotelName, hotelName)
                      .replace(placeholders.guestName, placeholders.guestNameReplacement)
                      .replace(
                        placeholders.guestLastName,
                        placeholders.guestLastNameReplacement
                      )
                      .replace(
                        placeholders.inviteLink,
                        placeholders.inviteLinkReplacement
                      )
                      .split('⌘⌘')
                      .map(p =>
                        p === placeholders.guestName ? (
                          <span className={styles.variable}>
                            {placeholders.guestNameValue}
                          </span>
                        ) : p === placeholders.guestLastName ? (
                          <span className={styles.variable}>
                            {placeholders.guestLastNameValue}
                          </span>
                        ) : (
                          p
                        )
                      )
                      .map(p =>
                        p === placeholders.inviteLink ? (
                          <span className={styles.variable}>
                            {placeholders.inviteUrl}
                          </span>
                        ) : (
                          p
                        )
                      )}
                </div>
                <div className={styles.progress}>
                  <span>
                    {status === statusTypes.Completed && (
                      <>
                        <div className={styles.icon}>
                          <CheckFlatIcon fill="#66BB6A" width="16px" height="16px" />
                        </div>

                        <div>
                          <div
                            className={styles.statusTitle}
                            style={{ color: '#4079F5' }}
                          >
                            Completed on{' '}
                            {moment(deliveryFinishDate).format('MMM DD, h:mm A')}
                          </div>
                          <div className={styles.statusDesc}>
                            {deliverySize - deliveryRemaining}/{deliverySize} sent
                          </div>
                        </div>
                      </>
                    )}
                    {status === statusTypes.Started && (
                      <>
                        <div className={styles.icon}>
                          <PaperplaneIcon width="23px" height="23px" fill="#4079F5" />
                        </div>

                        <div>
                          <div
                            className={styles.statusTitle}
                            style={{ color: '#4079F5' }}
                          >
                            Sending {deliverySize - deliveryRemaining}/{deliverySize}{' '}
                            messages
                          </div>
                          <div className={styles.statusDesc}>
                            approx. {deliveryTimeDiff} min remaining{' '}
                            <div className={styles.tooltipHolder}>
                              <InfoIcon fill="#9A8A8A" width="14px" height="14px" />
                              <div className={styles.tooltip}>
                                <p className={styles.tooltipTitle}>How does it work?</p>
                                <p className={styles.tooltipContent}>
                                  We send 30 messages at a time with 2 minute intervals.
                                  Sending the initial message may take up to 2 minutes.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    {status === statusTypes.Approved && (
                      <>
                        <div className={styles.icon}>
                          <ScheduledIcon width="18px" height="18px" />
                        </div>
                        <div>
                          <div
                            className={styles.statusTitle}
                            style={{ color: '#4079F5' }}
                          >
                            Scheduled for{' '}
                            {moment(deliveryStartDate).format('MMM DD, h:mm A')}
                          </div>
                          {recurringAnnouncementId ? (
                            <div className={styles.statusDesc}>
                              Recurring announcement
                              <div className={styles.tooltipHolder}>
                                <InfoIcon fill="#9A8A8A" width="14px" height="14px" />
                                <div className={styles.tooltip}>
                                  <p className={styles.tooltipTitle}>
                                    Recurring announcement
                                  </p>
                                  <p className={styles.tooltipContent}>
                                    <ScheduleDescription
                                      StartDate={deliveryStartDate}
                                      EndDate={deliveryFinishDate}
                                      RepeatKind={repeatKind}
                                      DaysOfWeeks={daysOfWeek}
                                    />
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className={styles.statusDesc}>
                              {deliverySize - deliveryRemaining}/{deliverySize} sent
                              <div className={styles.tooltipHolder}>
                                <InfoIcon fill="#9A8A8A" width="14px" height="14px" />
                                <div className={styles.tooltip}>
                                  <p className={styles.tooltipTitle}>How does it work?</p>
                                  <p className={styles.tooltipContent}>
                                    We send 30 messages at a time with 2 minute intervals.
                                    Sending the initial message may take up to 2 minutes.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    {status === statusTypes.Interrupted && (
                      <>
                        <div className={styles.icon}>
                          <CrossIcon width="16px" height="16px" />
                        </div>
                        <div>
                          <div className={styles.statusTitle}>
                            Cancelled on{' '}
                            {moment(deliveryFinishDate).format('MMM DD, h:mm A')}
                          </div>
                          <div className={styles.statusDesc}>
                            {deliverySize - deliveryRemaining}/{deliverySize} sent
                          </div>
                        </div>
                      </>
                    )}
                    {status === statusTypes.Deleted && (
                      <>
                        <div className={styles.icon}>
                          <CrossIcon width="16px" height="16px" />
                        </div>
                        <div>
                          <div className={styles.statusTitle}>Cancelled</div>
                          {recurringAnnouncementId && (
                            <div className={styles.statusDesc}>
                              Recurring announcement
                              <div className={styles.tooltipHolder}>
                                <InfoIcon fill="#9A8A8A" width="14px" height="14px" />
                                <div className={styles.tooltip}>
                                  <p className={styles.tooltipTitle}>
                                    Recurring announcement
                                  </p>
                                  <p className={styles.tooltipContent}>
                                    <ScheduleDescription
                                      StartDate={deliveryStartDate}
                                      EndDate={deliveryFinishDate}
                                      RepeatKind={repeatKind}
                                      DaysOfWeeks={daysOfWeek}
                                      prefix="Was scheduled for"
                                    />
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </span>
                  {status === statusTypes.Started && (
                    <>
                      <button
                        className={styles.cancelButton}
                        onClick={() => handleCancel()}
                      >
                        Cancel
                      </button>
                    </>
                  )}
                  {status === statusTypes.Approved && (
                    <>
                      <button
                        className={styles.cancelButton}
                        onClick={() =>
                          recurringAnnouncementId
                            ? setIsShowingCancelDialog(true)
                            : handleCancel()
                        }
                      >
                        Cancel
                      </button>
                      {recurringAnnouncementId && (
                        <button className={styles.editButton} onClick={onClickEdit}>
                          Edit
                        </button>
                      )}
                    </>
                  )}
                  {status === statusTypes.Completed &&
                    audience === 'DayBeforeCheckOut' && (
                      <button
                        onClick={() => handleClickShowResults()}
                        className={styles.cancelButton}
                      >
                        Show results
                      </button>
                    )}
                </div>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressBarInner}
                    style={{
                      width: `${
                        ((deliverySize - deliveryRemaining) * 100) / deliverySize
                      }%`
                    }}
                  ></div>
                </div>
              </div>
            </div>

            {focusedImage && (
              <ImageGallery
                hideGallery={() => setFocusedImage(null)}
                imgUrl={focusedImage}
              />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default ConversationBubble
